"use client";

import { createContext, useContext, useState, useCallback, useRef, ReactNode } from "react";

interface SendResult {
  creatorId?: number;
  creatorName: string;
  creatorPhotoUrl?: string | null;
  success: boolean;
  error?: string;
}

interface LastSendConfig {
  message: string;
  templateType: string;
  templateLanguage: string;
  scriptId?: string;
}

interface WhatsAppSendState {
  isActive: boolean;
  scriptId?: string;
  total: number;
  current: number;
  sent: number;
  failed: number;
  results: SendResult[];
  startTime?: Date;
  isComplete: boolean;
}

interface WhatsAppSendContextType {
  sendState: WhatsAppSendState;
  startSend: (
    scriptId: string | undefined,
    total: number,
    abort: () => void,
    message: string,
    templateType: string,
    templateLanguage: string,
    onDone?: () => void
  ) => void;
  completeSend: (sent: number, failed: number, results: SendResult[]) => void;
  cancelSend: () => void;
  dismiss: () => void;
  retrySend: () => void;
}

const initialState: WhatsAppSendState = {
  isActive: false,
  total: 0,
  current: 0,
  sent: 0,
  failed: 0,
  results: [],
  isComplete: false,
};

const WhatsAppSendContext = createContext<WhatsAppSendContextType | undefined>(undefined);

export function WhatsAppSendProvider({ children }: { children: ReactNode }) {
  const [sendState, setSendState] = useState<WhatsAppSendState>(initialState);
  const abortRef = useRef<(() => void) | null>(null);
  const onDoneRef = useRef<(() => void) | null>(null);
  const lastConfigRef = useRef<LastSendConfig | null>(null);
  // Keep a stable ref to current results for retrySend
  const resultsRef = useRef<SendResult[]>([]);

  const startSend = useCallback((
    scriptId: string | undefined,
    total: number,
    abort: () => void,
    message: string,
    templateType: string,
    templateLanguage: string,
    onDone?: () => void
  ) => {
    abortRef.current = abort;
    onDoneRef.current = onDone ?? null;
    lastConfigRef.current = { message, templateType, templateLanguage, scriptId };
    resultsRef.current = [];
    setSendState({
      isActive: true,
      scriptId,
      total,
      current: 0,
      sent: 0,
      failed: 0,
      results: [],
      startTime: new Date(),
      isComplete: false,
    });
  }, []);

  const completeSend = useCallback((sent: number, failed: number, results: SendResult[]) => {
    abortRef.current = null;
    resultsRef.current = results;
    setSendState(prev => ({
      ...prev,
      sent,
      failed,
      current: prev.total,
      results,
      isComplete: true,
    }));
  }, []);

  const cancelSend = useCallback(() => {
    abortRef.current?.();
    abortRef.current = null;
    setSendState(prev => ({ ...prev, isComplete: true }));
  }, []);

  const dismiss = useCallback(() => {
    onDoneRef.current?.();
    onDoneRef.current = null;
    lastConfigRef.current = null;
    resultsRef.current = [];
    setSendState(initialState);
  }, []);

  const retrySend = useCallback(() => {
    const config = lastConfigRef.current;
    const failedIds = resultsRef.current
      .filter(r => !r.success && r.creatorId != null)
      .map(r => r.creatorId as number);

    if (!failedIds.length || !config) return;

    const controller = new AbortController();
    abortRef.current = () => controller.abort();
    resultsRef.current = [];

    setSendState(prev => ({
      ...prev,
      total: failedIds.length,
      sent: 0,
      failed: 0,
      current: 0,
      results: [],
      startTime: new Date(),
      isComplete: false,
    }));

    fetch('/api/whatsapp-web/send-bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      signal: controller.signal,
      body: JSON.stringify({
        creatorIds: failedIds,
        templateType: config.templateType,
        templateLanguage: config.templateLanguage,
        customMessage: config.message,
        scriptId: config.scriptId,
      }),
    })
      .then(res => res.json())
      .then(data => {
        abortRef.current = null;
        const sent = data.sent || 0;
        const failed = data.failed || 0;
        const results = data.results || [];
        resultsRef.current = results;
        setSendState(prev => ({
          ...prev,
          sent,
          failed,
          current: failedIds.length,
          results,
          isComplete: true,
        }));
      })
      .catch(err => {
        abortRef.current = null;
        setSendState(prev => ({
          ...prev,
          failed: failedIds.length,
          current: failedIds.length,
          isComplete: true,
        }));
      });
  }, []);

  return (
    <WhatsAppSendContext.Provider value={{ sendState, startSend, completeSend, cancelSend, dismiss, retrySend }}>
      {children}
    </WhatsAppSendContext.Provider>
  );
}

export function useWhatsAppSend() {
  const context = useContext(WhatsAppSendContext);
  if (context === undefined) {
    throw new Error("useWhatsAppSend must be used within a WhatsAppSendProvider");
  }
  return context;
}
